/**
 * Utility functions for handling dynamic placeholders in text content
 */

/**
 * Helper function to check if a value is empty or undefined
 * @param {*} value - The value to check
 * @returns {boolean} - True if the value is empty or undefined
 */
export const isEmpty = (value) => !value || value.trim() === "";

/**
 * Helper function to check if a value is a 5-digit string (zip code)
 * @param {*} value - The value to check
 * @returns {boolean} - True if the value is a 5-digit string
 */
export const isFiveDigits = (value) => /^\d{5}$/.test(value);

/**
 * Creates legacy placeholder values from context and props
 * @param {Object} params - Parameters object
 * @param {Object} params.appliedConfiguration - Applied configuration from context
 * @param {string} params.resultname - Result name prop
 * @param {string} params.channelName - Channel name prop
 * @param {string} params.hierarchyShortName - Hierarchy short name prop
 * @param {string} params.stateName - State name prop
 * @param {string} params.searchTermDisplay - Search term display from context
 * @param {string} params.benefitType - Benefit type from context
 * @param {string} params.indicationName - Indication name from configuration
 * @param {string} params.zipCode - Zip Code Prop
 * @returns {Object} - Legacy placeholder values object
 */
export const createLegacyPlaceholderValues = ({
  appliedConfiguration,
  resultname,
  channelName,
  hierarchyShortName,
  stateName,
  searchTermDisplay,
  benefitType,
  indicationName,
  zipCode
}) => {
  return {
    '[product]': appliedConfiguration?.brandName || "",
    '[result]': resultname || "",
    '[channel]': channelName || "",
    '[indication]': indicationName || "",
    '[hierarchy]': hierarchyShortName || "",
    '[geography]': searchTermDisplay || stateName || zipCode || "",
    '[benefitType]': benefitType || "",
    '[benefit]': benefitType || "",
    '[state]': stateName || "",
    "[zip]": zipCode || ""
  };
};

/**
 * Merges legacy placeholders with dynamic placeholders
 * @param {Object} legacyPlaceholders - Legacy placeholder values
 * @param {Object} dynamicPlaceholders - Dynamic placeholder values
 * @returns {Object} - Merged placeholder values object
 */
export const mergePlaceholders = (legacyPlaceholders, dynamicPlaceholders = {}) => {
  const dynamicWithBrackets = Object.fromEntries(
    Object.entries(dynamicPlaceholders).map(([key, value]) => [`[${key}]`, value || ""])
  );
  
  return {
    ...legacyPlaceholders,
    ...dynamicWithBrackets
  };
};

/**
 * Helper function to get placeholder value and check if it's empty
 * @param {Object} allPlaceholderValues - All placeholder values
 * @param {string} placeholderKey - The placeholder key to check
 * @returns {Object} - Object with value and isEmpty properties
 */
export const getPlaceholderInfo = (allPlaceholderValues, placeholderKey) => {
  const value = allPlaceholderValues[placeholderKey] || "";
  return {
    value,
    isEmpty: isEmpty(value)
  };
};

/**
 * Handles dash-separated sequences for backward compatibility
 * @param {string} input - The input string to process
 * @param {Object} allPlaceholderValues - All placeholder values
 * @returns {string} - Processed string with dash-separated sequences handled
 */
export const handleDashSeparatedSequences = (input, allPlaceholderValues) => {
  const legacyPlaceholderKeys = ['product', 'result', 'channel', 'indication', 'geography', 'benefitType', 'benefit', 'hierarchy', 'state', 'zip'];
  const legacyPlaceholderPattern = new RegExp(`(\\[(?:${legacyPlaceholderKeys.join('|')})\\](?:-\\[(?:${legacyPlaceholderKeys.join('|')})\\])*)`, 'g');
  
  return input.replace(legacyPlaceholderPattern, (match) => {
    // Split the match by '-' to get individual placeholders
    const segments = match.split('-');

    // Filter out segments where certain placeholders are empty (as per requirement)
    const filteredSegments = segments.filter(segment => {
      const placeholderInfo = getPlaceholderInfo(allPlaceholderValues, segment);
      if ((segment === '[result]' || segment === '[channel]' || segment === '[hierarchy]' || segment === '[state]') 
          && placeholderInfo.isEmpty) {
        return false;
      }
      return true;
    });
    
    if (filteredSegments.length === 0) return '';
    return filteredSegments.join('-');
  });
};

/**
 * Removes empty placeholders from the input string
 * @param {string} input - The input string to process
 * @param {Object} allPlaceholderValues - All placeholder values
 * @returns {string} - Processed string with empty placeholders removed
 */
export const removeEmptyPlaceholders = (input, allPlaceholderValues) => {
  let processedInput = input;
  
  // List of placeholders to remove if they are empty
  const placeholdersToRemoveIfEmpty = ['[result]', '[channel]', '[hierarchy]', '[state]'];
  
  placeholdersToRemoveIfEmpty.forEach(placeholder => {
    const placeholderInfo = getPlaceholderInfo(allPlaceholderValues, placeholder);
    if (placeholderInfo.isEmpty) {
      const regex = new RegExp(placeholder.replace(/[[\]]/g, '\\$&'), 'g');
      processedInput = processedInput.replace(regex, '');
    }
  });
  
  return processedInput;
};

/**
 * Replaces all placeholders with their values
 * @param {string} input - The input string to process
 * @param {Object} allPlaceholderValues - All placeholder values
 * @returns {string} - Processed string with placeholders replaced
 */
export const replacePlaceholders = (input, allPlaceholderValues) => {
  let processedInput = input;
  
  Object.entries(allPlaceholderValues).forEach(([placeholder, value]) => {
    const regex = new RegExp(placeholder.replace(/[[\]]/g, '\\$&'), 'g');
    let replaceValue = value;
    if (placeholder.includes("hierarchy")) {
      if (isUpperCase(placeholder)) {
        replaceValue = value.toUpperCase();
      }
      else if (isLowerCase(placeholder)) {
        replaceValue = value.toLowerCase();
      }
    }

    processedInput = processedInput.replace(regex, replaceValue);
  });
  
  return processedInput;
};

const isUpperCase = (str) => {
  return str === String(str).toUpperCase();
};

const isLowerCase = (str) => {
  return str === String(str).toLowerCase();
};

/**
 * Cleans up the processed string by removing extra spaces, dashes, etc.
 * @param {string} input - The input string to clean up
 * @returns {string} - Cleaned up string
 */
export const cleanupProcessedString = (input) => {
  return input
    .replace(/--+/g, '-') // Replace multiple consecutive dashes with single dash
    .replace(/^-+|-+$/g, '') // Remove leading and trailing dashes
    .replace(/\s*-\s*$/g, '') // Remove trailing dash with optional spaces
    .replace(/^\s*-\s*/g, '') // Remove leading dash with optional spaces
    .replace(/\s{2,}/g, ' ') // Replace multiple spaces with single space
    .replace(/\s+([,.;:!?])/g, '$1') // Remove space before punctuation
    .replace(/([,.;:!?])\s*$/g, '$1') // Clean up trailing punctuation
    .trim();
};

/**
 * Main function to process dynamic placeholders in input string
 * @param {string} input - The input string containing placeholders
 * @param {Object} params - Parameters object
 * @param {Object} params.appliedConfiguration - Applied configuration from context
 * @param {string} params.resultname - Result name prop
 * @param {string} params.channelName - Channel name prop
 * @param {string} params.hierarchyShortName - Hierarchy short name prop
 * @param {string} params.stateName - State name prop
 * @param {string} params.searchTermDisplay - Search term display from context
 * @param {string} params.benefitType - Benefit type from context
 * @param {string} params.indicationName - Indication name from configuration
 * @param {Object} params.dynamicPlaceholders - Dynamic placeholder values
 * @returns {string} - Processed string with all placeholders replaced
 */
export const processDynamicPlaceholders = (input, {
  appliedConfiguration,
  resultname,
  channelName,
  hierarchyShortName,
  stateName,
  searchTermDisplay,
  benefitType,
  indicationName,
  dynamicPlaceholders = {},
  zipCode
}) => {
  // Create legacy placeholder values
  const legacyPlaceholderValues = createLegacyPlaceholderValues({
    appliedConfiguration,
    resultname,
    channelName,
    hierarchyShortName,
    stateName,
    searchTermDisplay,
    benefitType,
    indicationName,
    zipCode
  });

  // Merge legacy and dynamic placeholders
  const allPlaceholderValues = mergePlaceholders(legacyPlaceholderValues, dynamicPlaceholders);

  // Process the input string
  let processedInput = input;
  
  // Handle dash-separated sequences
  processedInput = handleDashSeparatedSequences(processedInput, allPlaceholderValues);
  
  // Remove empty placeholders
  processedInput = removeEmptyPlaceholders(processedInput, allPlaceholderValues);
  
  // Replace all placeholders with their values
  processedInput = replacePlaceholders(processedInput, allPlaceholderValues);
  
  // Clean up the processed string
  processedInput = cleanupProcessedString(processedInput);
  
  return processedInput;
};
